import {
  Button,
  IconButton,
  RadioGroup,
  RadioGroupItem,
  Typography,
} from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import { IntervalType } from "api/resources/suraasaPlus/types"
import clsx from "clsx"
import SuraasaPlusLogo, {
  LogoVariant,
} from "features/SuraasaPlus/components/SuraasaPlusLogo"
import { AnimatePresence, motion } from "framer-motion"
import { Xmark } from "iconoir-react"
import { useState } from "react"
import { SURAASA_PLUS_SUBSCRIPTION_PRODUCT_SLUG } from "utils/constants"
import { mapErrorsV2 } from "utils/helpers"
import useFormatPrice from "utils/hooks/useFormatPrice"

import lightOverlay from "../assets/light_layer.webp"
import darkOverlay from "../assets/top_dark_layer.webp"
import { DialogThemeMode } from "../utils"
import AdaptiveDialogAndSheets from "./AdaptiveDialogAndSheets"

type Props = {
  open: boolean
  handleClose: (selectedProductSlug?: string) => void
  mode?: DialogThemeMode
}

enum PlanMode {
  MONTHLY = "monthly",
  ANNUAL = "annual",
}

const modeStyles = {
  [DialogThemeMode.dark]: {
    body: "bg-[#0E1525]",
    header:
      "bg-[linear-gradient(180deg,#4C1278_0%,#082743_56.82%,#0E1525_100%)]",
    footer: "bg-[#0E1525]",
    selectedBg: "bg-[#272E3D]",
    text: {
      plus: "#FFF7C3",
      primary: "text-white",
      secondary: "text-onSurface-400",
      planHeading: "text-white",
      planPrice: "text-white",
      currentPlan: "text-onSurface-400",
    },
    buttons: {
      x: "text-surface hover:bg-onSurface-200/20  active:bg-onSurface-300/40 active:text-onSurface-900",
      text: "text-white hover:bg-white/20 active:bg-white/30",
      primary: "bg-surface text-onSurface-900",
      radio:
        "data-[state=checked]:bg-surface [&>span>div]:!bg-[#272E3E] data-[state=checked]:border-surface",
    },
  },
  [DialogThemeMode.light]: {
    body: "bg-white",
    header: "bg-white",
    footer: "bg-gradient-to-b from-white/0 via-white to-white",
    selectedBg: "bg-[#F1F5F9]",
    text: {
      plus: "#83806A",
      primary: "text-onSurface-900",
      secondary: "text-onSurface-500",
      planHeading: "text-onSurface-700",
      planPrice: "text-onSurface-900",
      currentPlan: "text-onSurface-600",
    },
    buttons: {
      x: "text-onSurface-700 hover:bg-gray-100 active:bg-gray-200",
      text: "text-onSurface-700 hover:bg-gray-100 active:bg-gray-200",
      primary: "bg-onSurface-900 text-white",
      radio: "",
    },
  },
}

const getPlanButtonClasses = (
  plan: PlanMode,
  selectedPlan: PlanMode,
  mode: DialogThemeMode
) => {
  const isSelected = selectedPlan === plan
  return clsx(
    "flex cursor-pointer flex-col items-start justify-between rounded-xl p-2.5 outline transition-all duration-200 ease-in-out hover:shadow-md active:scale-95",
    {
      "outline-onSurface-300": mode === DialogThemeMode.light,
      "outline-onSurface-500":
        mode === DialogThemeMode.dark && plan === PlanMode.MONTHLY,
      "outline-onSurface-600":
        mode === DialogThemeMode.dark && plan !== PlanMode.MONTHLY,
      "outline-2 !outline-onSurface-600": isSelected,
      "bg-onSurface-100": isSelected && mode === DialogThemeMode.light,
      "bg-[#262C39]": isSelected && mode === DialogThemeMode.dark,
    }
  )
}

const planMap: Partial<
  Record<IntervalType, { label: string; planMode: PlanMode; tag?: string }>
> = {
  [IntervalType.year]: {
    label: "ANNUAL PLAN",
    planMode: PlanMode.ANNUAL,
    tag: "BEST VALUE",
  },
  [IntervalType.month]: {
    label: "MONTHLY PLAN",
    planMode: PlanMode.MONTHLY,
  },
}

const SelectYourPlanDialog = ({
  open,
  handleClose,
  mode = DialogThemeMode.dark,
}: Props) => {
  const styles = modeStyles[mode]
  const [selectedPlan, setSelectedPlan] = useState<PlanMode>(PlanMode.ANNUAL)
  const overlayImg = mode === DialogThemeMode.dark ? darkOverlay : lightOverlay
  const formatPrice = useFormatPrice()

  const productDetailsQuery = useQuery({
    enabled: open,
    queryKey: queries.suraasaPlus.productDetails({
      slug: SURAASA_PLUS_SUBSCRIPTION_PRODUCT_SLUG,
    }).queryKey,
    queryFn: () =>
      api.suraasaPlus.retrieveProductGroup({
        urlParams: { slug: SURAASA_PLUS_SUBSCRIPTION_PRODUCT_SLUG },
      }),
    onError: e => mapErrorsV2(e),
  })

  const products = productDetailsQuery.data?.products ?? []

  // Ensure Annual is always first
  const orderedProducts = [...products].sort((a, b) => {
    const order: Record<IntervalType, number> = {
      [IntervalType.year]: 0,
      [IntervalType.month]: 1,
      [IntervalType.week]: 2,
      [IntervalType.day]: 3,
    }
    return (
      (order[a.subscriptionDetails?.intervalType as IntervalType] ?? 99) -
      (order[b.subscriptionDetails?.intervalType as IntervalType] ?? 99)
    )
  })

  const selectedProductSlug = products.find(
    (p: any) =>
      planMap[p.subscriptionDetails?.intervalType]?.planMode === selectedPlan
  )?.slug

  const currencySymbol = products[0]?.price?.currency?.symbol ?? "" // symbol from first product

  return (
    <AdaptiveDialogAndSheets
      sheetHeight={87}
      open={open}
      handleClose={handleClose}
      contentClassName="sm:!w-[430px] overflow-hidden rounded-xl"
      bodyClassName={clsx(styles.body, "p-0")}
      body={
        <>
          {/* Header */}
          <div className={clsx(styles.header, "relative py-3 pl-3 pr-2")}>
            <img
              src={overlayImg}
              alt=""
              className="pointer-events-none absolute -top-px left-0"
            />
            <div className="mb-2 flex justify-between">
              <SuraasaPlusLogo
                variant={LogoVariant.plus}
                height={20}
                width={44}
                color={styles.text.plus}
              />
              <IconButton
                className={clsx("rounded-lg", styles.buttons.x)}
                onClick={() => handleClose()}
              >
                <Xmark className="!size-2.5" strokeWidth={2} />
              </IconButton>
            </div>
            <div>
              <Typography
                variant="title2"
                className={clsx("w-fit", styles.text.primary)}
              >
                Select a plan
              </Typography>
              <Typography
                className={clsx("mt-1", styles.text.secondary)}
                variant="body"
              >
                Please select a plan below
              </Typography>
            </div>
          </div>

          {/* Loading skeleton */}
          {productDetailsQuery.isLoading ? (
            <div
              className={clsx(
                styles.body,
                "flex flex-col gap-2.5 px-3 pb-3 pt-1"
              )}
            >
              {[1, 2].map(i => (
                <div
                  key={i}
                  className={clsx(
                    "flex animate-pulse flex-col items-start rounded-xl border-2 p-2.5",
                    styles.selectedBg
                  )}
                >
                  <div className="mb-2 h-2 w-20 rounded bg-onSurface-300/30" />
                  <div className="mb-1 h-3 w-20 rounded bg-onSurface-300/30" />
                  <div className="flex gap-2">
                    <div className="h-1.5 w-10 rounded bg-onSurface-300/30" />
                    <div className="h-1.5 w-10 rounded bg-onSurface-300/30" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <RadioGroup
              value={selectedPlan}
              onValueChange={val => setSelectedPlan(val as PlanMode)}
              className={clsx(
                styles.body,
                "flex flex-col gap-2.5 px-3 pb-3 pt-1"
              )}
            >
              {orderedProducts.map((product: any) => {
                const intervalType = product.subscriptionDetails?.intervalType
                const config = planMap[intervalType as IntervalType]
                if (!config) return null

                const price = product.price?.price ?? 0
                const mrp = product.price?.mrp ?? 0
                const savings = mrp - price

                return (
                  <button
                    key={product.id}
                    onClick={() => setSelectedPlan(config.planMode)}
                    className={getPlanButtonClasses(
                      config.planMode,
                      selectedPlan,
                      mode
                    )}
                  >
                    <div className="flex w-full items-start justify-between">
                      <div className="mb-1.5 flex items-center gap-1">
                        <Typography
                          variant="preTitle"
                          className={clsx(
                            "shrink-0 !text-base uppercase",
                            styles.text.planHeading
                          )}
                        >
                          {config.label}
                        </Typography>
                        {config.tag && (
                          <Typography className="flex shrink-0 items-center justify-center rounded-full bg-success-500 px-1 py-0.5 text-[10px] text-white sm:text-preTitle">
                            {config.tag}
                          </Typography>
                        )}
                      </div>
                      <RadioGroupItem
                        className={clsx("size-2.5", styles.buttons.radio)}
                        value={config.planMode}
                        checked={selectedPlan === config.planMode}
                      />
                    </div>

                    {savings > 0 && (
                      <div className="flex items-center gap-1">
                        <Typography className="bg-[linear-gradient(90deg,#B7B7B7_0%,#64748B_100%)] bg-clip-text text-base text-transparent line-through decoration-muted">
                          {currencySymbol + formatPrice(mrp)}
                        </Typography>
                        <Typography
                          className="text-success-400"
                          variant="strongSmallBody"
                        >
                          Save {currencySymbol + formatPrice(savings)}
                        </Typography>
                      </div>
                    )}

                    <div className="mt-0.25 flex w-full items-end justify-between">
                      <Typography
                        className={clsx(
                          "w-fit text-start text-title1Mobile",
                          styles.text.planPrice
                        )}
                      >
                        {currencySymbol + formatPrice(price)}
                      </Typography>
                      <AnimatePresence>
                        {selectedPlan === config.planMode && (
                          <motion.div
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 6 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Typography
                              variant="smallBody"
                              className={clsx(
                                "shrink-0",
                                styles.text.currentPlan
                              )}
                            >
                              Selected Plan
                            </Typography>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </button>
                )
              })}
            </RadioGroup>
          )}

          {/* Footer */}
          <div
            className={clsx(
              "flex w-full flex-col-reverse items-center justify-end gap-2 border-0 px-3 pb-3 sm:flex-row sm:gap-3",
              styles.footer
            )}
          >
            <Button
              variant="text"
              className={clsx("w-full p-1.5 sm:w-fit", styles.buttons.text)}
              onClick={() => handleClose()}
            >
              <Typography variant="strong">Cancel</Typography>
            </Button>

            <Button
              variant="filled"
              className={clsx(
                "w-full rounded-xl px-2 py-1.5 font-semibold sm:w-fit",
                styles.buttons.primary
              )}
              disabled={productDetailsQuery.isLoading}
              onClick={() => handleClose(selectedProductSlug)}
            >
              Continue
            </Button>
          </div>
        </>
      }
    />
  )
}

export default SelectYourPlanDialog
